import { Dialog } from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useRecoilState } from "recoil";
import { WarningIcon } from "../../assets/iconComponents/Warning";
import { currentNewRequisitionEdit } from "../../recoil/atoms";
import { NewRequisitionForm } from "../../recoil/types";
import { Button } from "../shared/Button/Button";
import NewReqRequiredFieldsMessage from "./NewReqRequiredFieldsMessage";
import {
  EditOrderDialogContent,
  EditOrderSaveButtonSlot,
  NewReqRequiredMessage,
  OrderDetailsContent,
  StepperButtonMobile,
  StepperPanelDesktop,
  StepperPanelMobile,
} from "./NewRequisition.styles";
import { OrderDetailsDialogTitle } from "./OrderDetailsDialog";

export const EditFormDialog: React.FC<{
  id: string;
  title: string;
  titleExtra?: ReactNode;
  submitLabel: string;
  originalForm: NewRequisitionForm;
  getChanges: (form: NewRequisitionForm) => object;
  getValidationError: (form: NewRequisitionForm) => boolean;
  onSubmit: (form: NewRequisitionForm) => Promise<string | void>;
  onSubmitClicked?: () => void;
  onClose: () => void;
  children: ReactNode;
}> = ({
  id,
  title,
  titleExtra,
  submitLabel,
  originalForm,
  getChanges,
  getValidationError,
  onSubmit,
  onSubmitClicked,
  onClose,
  children,
}) => {
  const [requisitionEdit, setRequisitionEdit] = useRecoilState(
    currentNewRequisitionEdit,
  );
  const dialogContentRef = useRef<HTMLDivElement | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const form = requisitionEdit.requisitionForm;
  const isChanged = Object.keys(getChanges(form)).length > 0;
  const hasValidationError = getValidationError(form);
  const showValidationError = requisitionEdit.isError && hasValidationError;

  useEffect(() => {
    setSubmitErrorMessage("");
    setRequisitionEdit({
      preflight: false,
      isError: false,
      isDraftError: false,
      requisitionForm: originalForm,
    });
  }, [originalForm, setRequisitionEdit]);

  useEffect(() => {
    if (!requisitionEdit.isError || hasValidationError) return;
    setRequisitionEdit((currentEdit) => ({ ...currentEdit, isError: false }));
  }, [hasValidationError, requisitionEdit.isError, setRequisitionEdit]);

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      if (dialogContentRef.current) {
        dialogContentRef.current.scrollTop = 0;
      }
    });
  };

  const handleSubmit = async () => {
    onSubmitClicked?.();
    setRequisitionEdit((currentEdit) => ({
      ...currentEdit,
      isError: hasValidationError,
    }));
    setSubmitErrorMessage("");
    if (hasValidationError) {
      scrollToTop();
      return;
    }

    setLoading(true);
    const errorMessage = await onSubmit(form);
    setLoading(false);
    if (errorMessage) {
      setSubmitErrorMessage(errorMessage);
      scrollToTop();
    }
  };

  const backButton = (
    <Button
      disabled={loading}
      label="BACK"
      reverse
      size="medium"
      onClick={onClose}
    />
  );
  const submitButton = (
    <EditOrderSaveButtonSlot>
      <Button
        disabled={!isChanged || loading}
        label={submitLabel}
        reverse={!isChanged}
        size="medium"
        loading={loading}
        onClick={handleSubmit}
      />
    </EditOrderSaveButtonSlot>
  );

  return (
    <Dialog id={id} fullWidth maxWidth="md" open>
      <OrderDetailsDialogTitle
        id={`${id}-title`}
        disabled={loading}
        onClose={onClose}
        title={title}
      >
        {titleExtra}
      </OrderDetailsDialogTitle>
      <EditOrderDialogContent ref={dialogContentRef}>
        <OrderDetailsContent>
          {showValidationError && <NewReqRequiredFieldsMessage isError />}
          {submitErrorMessage && (
            <NewReqRequiredMessage $isError>
              <WarningIcon testId="WarningIcon" />
              {submitErrorMessage}
            </NewReqRequiredMessage>
          )}
          {children}
        </OrderDetailsContent>
      </EditOrderDialogContent>
      <StepperPanelDesktop>
        {backButton}
        {submitButton}
      </StepperPanelDesktop>
      <StepperPanelMobile>
        <StepperButtonMobile>
          {backButton}
          {submitButton}
        </StepperButtonMobile>
      </StepperPanelMobile>
    </Dialog>
  );
};
